import { NineHeading } from "@9amhealth/wcl/generated/react";
import { useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import { FC, useCallback } from "react";
import translate from "src/lib/translate";
import OnEvent from "src/ui/components/OnEvent/OnEvent";
import Translate from "src/ui/components/Translate/Translate";
import {
  ChatUploadLabResultsCubit,
  ProviderChoice,
  Step
} from "../ChatUploadLabResultsCubit";
import { LabReportUploadRequest } from "@9amhealth/openapi";
import { InfoText, StepContent } from "../components";

const ProviderOptions = styled.div`
  width: 100%;
`;

interface ProviderSelectionScreenProps {
  uploadFilesAndShowStep: (
    step: Step,
    provider: LabReportUploadRequest["provider"] | undefined
  ) => Promise<void>;
}

const PROVIDER_ENUM_MAP: Record<
  ProviderChoice,
  LabReportUploadRequest["provider"] | undefined
> = {
  labcorp: LabReportUploadRequest.provider.LABCORP,
  quest: LabReportUploadRequest.provider.QUEST,
  none: undefined
};

export const ProviderSelectionScreen: FC<ProviderSelectionScreenProps> = ({
  uploadFilesAndShowStep
}) => {
  const [{ providerChoice }, { setCurrentStep, setProviderChoice }] =
    useBlocNext(ChatUploadLabResultsCubit);

  const handleProviderSelection = useCallback(
    (choice: ProviderChoice) => {
      setProviderChoice(choice);
      if (choice === "none") {
        setCurrentStep("howToGetReport");
        return;
      }

      void uploadFilesAndShowStep("goodNews", PROVIDER_ENUM_MAP[choice]);
    },
    [uploadFilesAndShowStep]
  );

  const handleProviderRadioChange = (
    e: CustomEvent<{ checked: boolean; name: string }>
  ) => {
    if (e.detail.checked) {
      handleProviderSelection(e.detail.name as ProviderChoice);
    }
  };

  const providerOptions: { label: string; choice: ProviderChoice }[] = [
    {
      label: translate("chat.labUpload.provider.labcorp"),
      choice: "labcorp"
    },
    {
      label: translate("chat.labUpload.provider.quest"),
      choice: "quest"
    },
    {
      label: translate("chat.labUpload.provider.none"),
      choice: "none"
    }
  ];

  return (
    <StepContent>
      <NineHeading>
        <h3 className="as-h4-large">
          <Translate msg="chat.labUpload.provider.title" />
        </h3>
      </NineHeading>
      <InfoText>
        <Translate msg="chat.labUpload.provider.description" />
      </InfoText>
      <ProviderOptions>
        <nine-answer-item-list direction="vertical">
          {providerOptions.map(({ label, choice }) => (
            <OnEvent
              key={choice}
              events={{
                nineInputChange: handleProviderRadioChange
              }}
            >
              <nine-answer-item
                value={label}
                name={choice}
                type="radio"
                padding="true"
                center="false"
                checked={providerChoice === choice ? "true" : "false"}
              >
                <strong>{label}</strong>
              </nine-answer-item>
            </OnEvent>
          ))}
        </nine-answer-item-list>
      </ProviderOptions>
    </StepContent>
  );
};
